// ─── Knowledge Capture Pipeline — IndexedDB Store ────────────────────────────
// Canonical persistence for KnowledgeItems, original file blobs, and user
// collections. IndexedDB (not localStorage) because captured items carry
// large extracted text and original binaries; quota is hundreds of MB.
//
// Object stores:
//   items        — KnowledgeItem records (keyPath: id)
//   blobs        — { id, blob } original files / recordings (keyPath: id)
//   collections  — UserCollection records (keyPath: id)

import type { KnowledgeItem, UserCollection } from '@/types/knowledgeItem';
import { COLLECTION_COLORS, KNOWLEDGE_EVENTS } from '@/types/knowledgeItem';
import * as FileStorageService from '@/services/fileStorage/FileStorageService';

const DB_NAME = 'nomad-knowledge';
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('This browser does not support local knowledge storage (IndexedDB).'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('items')) {
        const items = db.createObjectStore('items', { keyPath: 'id' });
        items.createIndex('dateAdded', 'dateAdded');
        items.createIndex('sourceHash', 'sourceHash');
      }
      if (!db.objectStoreNames.contains('blobs')) {
        db.createObjectStore('blobs', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('collections')) {
        db.createObjectStore('collections', { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Could not open knowledge storage.'));
  });
  return dbPromise;
}

function tx<T>(
  storeName: 'items' | 'blobs' | 'collections',
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then(
    db =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(storeName, mode);
        const req = fn(t.objectStore(storeName));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('Knowledge storage operation failed.'));
      }),
  );
}

function notifyUpdated(): void {
  window.dispatchEvent(new CustomEvent(KNOWLEDGE_EVENTS.updated));
}

export function generateKnowledgeId(prefix = 'ki'): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

// ─── Items ────────────────────────────────────────────────────────────────────

export async function putKnowledgeItem(item: KnowledgeItem): Promise<void> {
  await tx('items', 'readwrite', s => s.put(item));
  notifyUpdated();
}

export async function getKnowledgeItem(id: string): Promise<KnowledgeItem | null> {
  const result = await tx<KnowledgeItem | undefined>('items', 'readonly', s => s.get(id));
  return result ?? null;
}

/** All items, newest first. */
export async function getAllKnowledgeItems(): Promise<KnowledgeItem[]> {
  const all = await tx<KnowledgeItem[]>('items', 'readonly', s => s.getAll());
  return all.sort((a, b) => new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime());
}

export async function deleteKnowledgeItem(id: string): Promise<void> {
  await tx('items', 'readwrite', s => s.delete(id));
  try {
    await tx('blobs', 'readwrite', s => s.delete(id));
  } catch { /* blob may not exist */ }
  notifyUpdated();
}

export async function findItemBySourceHash(hash: string): Promise<KnowledgeItem | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction('items', 'readonly');
    const idx = t.objectStore('items').index('sourceHash');
    const req = idx.get(hash);
    req.onsuccess = () => resolve((req.result as KnowledgeItem | undefined) ?? null);
    req.onerror = () => reject(req.error ?? new Error('Lookup failed.'));
  });
}

export async function updateKnowledgeItem(
  id: string,
  updates: Partial<KnowledgeItem>,
): Promise<KnowledgeItem | null> {
  const existing = await getKnowledgeItem(id);
  if (!existing) return null;
  const next = { ...existing, ...updates };
  await putKnowledgeItem(next);
  return next;
}

// ─── Blobs (original files / recordings) ──────────────────────────────────────

export async function putKnowledgeBlob(id: string, blob: Blob): Promise<void> {
  await tx('blobs', 'readwrite', s => s.put({ id, blob }));
}

export async function getKnowledgeBlob(id: string): Promise<Blob | null> {
  const rec = await tx<{ id: string; blob: Blob } | undefined>('blobs', 'readonly', s => s.get(id));
  return rec?.blob ?? null;
}

// ─── Collections ──────────────────────────────────────────────────────────────

export async function listCollections(): Promise<UserCollection[]> {
  const all = await tx<UserCollection[]>('collections', 'readonly', s => s.getAll());
  return all.sort((a, b) => {
    if (a.favorite !== b.favorite) return a.favorite ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

export async function createCollection(
  name: string,
  opts?: Partial<Pick<UserCollection, 'color' | 'iconName' | 'aiSuggested'>>,
): Promise<UserCollection> {
  const existing = await listCollections();
  const match = existing.find(c => c.name.trim().toLowerCase() === name.trim().toLowerCase());
  if (match) return match;

  const collection: UserCollection = {
    id: generateKnowledgeId('col'),
    name: name.trim(),
    color: opts?.color ?? COLLECTION_COLORS[existing.length % COLLECTION_COLORS.length],
    iconName: opts?.iconName ?? 'folder',
    favorite: false,
    aiSuggested: opts?.aiSuggested ?? false,
    createdAt: new Date().toISOString(),
  };
  await tx('collections', 'readwrite', s => s.put(collection));
  notifyUpdated();
  return collection;
}

export async function renameCollection(id: string, name: string): Promise<void> {
  const all = await listCollections();
  const col = all.find(c => c.id === id);
  if (!col) return;
  await tx('collections', 'readwrite', s => s.put({ ...col, name: name.trim() }));
  notifyUpdated();
}

export async function toggleCollectionFavorite(id: string): Promise<void> {
  const all = await listCollections();
  const col = all.find(c => c.id === id);
  if (!col) return;
  await tx('collections', 'readwrite', s => s.put({ ...col, favorite: !col.favorite }));
  notifyUpdated();
}

/** Delete a collection; items keep existing but lose the membership. */
export async function deleteCollection(id: string): Promise<void> {
  await tx('collections', 'readwrite', s => s.delete(id));
  const items = await getAllKnowledgeItems();
  for (const item of items) {
    if (item.collections.includes(id)) {
      await tx('items', 'readwrite', s =>
        s.put({ ...item, collections: item.collections.filter(c => c !== id) }),
      );
    }
  }
  notifyUpdated();
}

/** Merge `sourceId` into `targetId`: move memberships, remove source. */
export async function mergeCollections(sourceId: string, targetId: string): Promise<void> {
  if (sourceId === targetId) return;
  const items = await getAllKnowledgeItems();
  for (const item of items) {
    if (item.collections.includes(sourceId)) {
      const next = Array.from(new Set([...item.collections.filter(c => c !== sourceId), targetId]));
      await tx('items', 'readwrite', s => s.put({ ...item, collections: next }));
    }
  }
  await tx('collections', 'readwrite', s => s.delete(sourceId));
  notifyUpdated();
}

export async function setItemCollections(itemId: string, collectionIds: string[]): Promise<void> {
  await updateKnowledgeItem(itemId, { collections: collectionIds });
}

export async function countItemsInCollection(collectionId: string): Promise<number> {
  const items = await getAllKnowledgeItems();
  return items.filter(i => i.collections.includes(collectionId)).length;
}

// ─── Stats (Learn card + Search) ──────────────────────────────────────────────

export interface KnowledgeStats {
  itemCount: number;
  collectionCount: number;
  storageBytes: number;
  timeSavedMin: number;
}

export async function getKnowledgeStats(): Promise<KnowledgeStats> {
  const [items, collections] = await Promise.all([getAllKnowledgeItems(), listCollections()]);
  const storageBytes = items.reduce(
    (sum, i) => sum + (i.fileSize ?? 0) + i.rawContent.length,
    0,
  );
  // Build 2.32.0: Time Saved must only increase after real AI processing has
  // completed. Uploading a file does NOT save time — only when OCR, summarization,
  // or analysis has actually run (processingStatus === 'complete' AND the item
  // has a non-zero wordCount indicating real content was extracted).
  // The previous `|| complete * 5` fallback fabricated 5 min per uploaded file
  // even when no processing had occurred. Removed.
  const timeSavedMin = items.reduce(
    (sum, i) =>
      sum + (
        i.processingStatus === 'complete' && i.readingTimeMin > 0
          ? Math.min(i.readingTimeMin, 30) + 4
          : 0
      ),
    0,
  );

  const usedCollectionIds = new Set(items.flatMap(i => i.collections));
  const collectionCount = collections.filter(c => usedCollectionIds.has(c.id)).length;

  return { itemCount: items.length, collectionCount, storageBytes, timeSavedMin };
}

// ─── Cloudflare R2 blob storage (Sprint 091 — Cloud Storage Migration™) ───────

/**
 * Upload a blob to Cloudflare R2 via the FileStorageService and return the
 * file_objects.id for later retrieval. Throws if the upload fails so callers
 * can fall back to IndexedDB.
 *
 * @param itemId   KnowledgeItem.id — used to build the storage key name.
 * @param blob     Original file or audio blob to store.
 * @returns        R2 file_objects.id (store this as KnowledgeItem.r2FileId).
 */
export async function putKnowledgeBlobToR2(itemId: string, blob: Blob): Promise<number> {
  const ext  = blob.type ? blob.type.split('/')[1] ?? 'bin' : 'bin';
  const name = `knowledge/${itemId}.${ext}`;
  const record = await FileStorageService.uploadFile(blob, name, blob.type || undefined);
  return record.id;
}

/**
 * Fetch a blob from R2 by its file_objects.id.
 * Returns null if the file is missing, expired, or unreachable.
 */
export async function getKnowledgeBlobFromR2(r2FileId: number): Promise<Blob | null> {
  try {
    const { url } = await FileStorageService.getSignedUrl(r2FileId, 300);
    const res = await fetch(url);
    if (!res.ok) return null;
    return res.blob();
  } catch {
    return null;
  }
}

/** Wipe all knowledge data (Factory Reset). */
export async function clearKnowledgeStore(): Promise<void> {
  await tx('items', 'readwrite', s => s.clear());
  await tx('blobs', 'readwrite', s => s.clear());
  await tx('collections', 'readwrite', s => s.clear());
  notifyUpdated();
}
